import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CompanyShell } from "@/components/CompanyShell";
import { ScoreRing } from "@/components/ScoreRing";
import { Search, Loader2, Check, TriangleAlert, ArrowLeft, Trophy } from "lucide-react";

export default function FindCandidate() {
  const nav = useNavigate();
  const [form, setForm] = useState({ title: "", location: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/company/match", form);
      setResults(data.results);
      if (data.results.length === 0) toast.info("Vo vašej databáze zatiaľ nie sú žiadni kandidáti.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (results) {
    return (
      <CompanyShell>
        <button onClick={() => setResults(null)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4"><ArrowLeft size={16} /> Späť na hľadanie</button>
        <h1 className="font-head text-3xl font-bold tracking-tight">Najlepší kandidáti</h1>
        <p className="mt-2 text-slate-600">Pozícia: <span className="font-semibold text-slate-800">{form.title}</span></p>

        {results.length === 0 ? (
          <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">Vo vašej databáze nie sú kandidáti. Najprv nahrajte CV.</div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="match-results">
            {results.map((r, i) => (
              <div key={r.id} className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 fade-up">
                <div className="flex items-center gap-4">
                  {i === 0 && <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><Trophy size={18} className="text-amber-500" /></div>}
                  <img src={r.photo_url} alt={r.name} className="h-14 w-14 rounded-full object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-head font-semibold text-lg truncate">{r.name}</div>
                    <div className="text-sm text-slate-500 truncate">{r.title}</div>
                  </div>
                  <div className="text-right">
                    <div className={`font-head text-2xl font-bold ${r.match >= 90 ? "text-emerald-600" : r.match >= 60 ? "text-blue-700" : "text-amber-500"}`}>{r.match}%</div>
                    <div className="text-xs text-slate-400 uppercase tracking-wide">zhoda</div>
                  </div>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <div>
                    {(r.reasons || []).map((x, j) => (
                      <div key={j} className="flex gap-2 text-sm text-slate-700 mb-1.5"><Check size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />{x}</div>
                    ))}
                  </div>
                  <div>
                    {(r.missing || []).map((x, j) => (
                      <div key={j} className="flex gap-2 text-sm text-slate-500 mb-1.5"><TriangleAlert size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />{x}</div>
                    ))}
                  </div>
                </div>
                <button data-testid={`view-match-${r.id}`} onClick={() => nav(`/company/candidate/${r.id}`)}
                  className="mt-4 inline-flex items-center gap-2 bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors active:scale-95">
                  Zobraziť profil
                </button>
              </div>
            ))}
          </div>
        )}
      </CompanyShell>
    );
  }

  return (
    <CompanyShell>
      <h1 className="font-head text-3xl font-bold tracking-tight">Nájsť vhodného kandidáta</h1>
      <p className="mt-2 text-slate-600">Zadajte pozíciu a AI nájde najvhodnejších ľudí z vašej databázy.</p>

      <form onSubmit={run} className="mt-8 max-w-xl space-y-4">
        <FormField label="Pozícia" value={form.title} onChange={set("title")} required placeholder="napr. Skladník / Operátor výroby" testId="position-title" />
        <FormField label="Miesto" value={form.location} onChange={set("location")} placeholder="napr. Žilina" testId="position-location" />
        <label className="block">
          <span className="text-sm font-semibold text-slate-700">Požiadavky a popis pozície</span>
          <textarea data-testid="position-description" value={form.description} onChange={set("description")} required rows={6}
            placeholder="napr. práca vo výrobe, skúsenosť s VZV výhodou, manuálna zručnosť, ochota pracovať na zmeny…"
            className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-colors" />
        </label>
        <button data-testid="run-match-btn" disabled={busy}
          className="inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-8 py-4 font-semibold transition-colors active:scale-95 disabled:opacity-60">
          {busy ? <><Loader2 className="animate-spin" size={18} /> AI porovnáva…</> : <><Search size={18} /> Nájsť kandidátov</>}
        </button>
      </form>
    </CompanyShell>
  );
}

function FormField({ label, value, onChange, placeholder, required, testId }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <input data-testid={testId} value={value} onChange={onChange} placeholder={placeholder} required={required}
        className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-colors" />
    </label>
  );
}
